import React, { useCallback, useEffect, useState, useMemo } from 'react';
import styles from './MultipleVideosPlayer.module.scss';
import cx from 'classnames';
import VideoSubtitles from '../VideoSubtitles/VideoSubtitles';
import { parseSync } from 'subtitle';
import VideoService from '../../service/VideoService';
import TransitionSimulation from '../TransitionSimulation/TransitionSimulation';
import VideoLogo from '../VideoLogo/VideoLogo';
import {
  getPlayerMixedOrientation,
  calculateVideoOrientation,
  generateTextPanelsArray,
  generateTransitionsArray,
  getMaxDimensions,
} from '../../utils/SimulatedItemsUtility';
import ResourceHelper from '../../utils/ResourceHelper';
import VideoCallToAction from '../VideoCallToAction/VideoCallToAction';
import PlayerComponent from './PlayerComponent';
import { useTrimContext } from '../../context/VideoTrimContex';
import SubtitlesHelper from '../../utils/SubtitlesHelper';
import useBackgroundMusic from '../../features/Editor/hooks/useBackgroundMusic';
import { useUserContext } from 'engage/context/UserContext';
import { useNodeRef } from 'engage/hooks/useNodeRef';

const MultipleVideosPlayer = ({
  videoElements,
  currentPlayerKey,
  scenes,
  transitions,
  videoIndex,
  handleEnded,
  className,
  customTiming,
  logo,
  intro,
  outro,
  isDraft,
  draftComponent,
  rerecordComponent,
  isStopBackgroundMusic,
  userPermissions,
  miniRightSidebar,
  isEditingRightSidebar,
  videoConfig
}) => {
  const [volume, setVolume] = useState({ current: 1, previously: 1 });
  const [isControlsVisible, setIsControlsVisible] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [subtitlesTextForShowing, setSubtitlesTextForShowing] = useState('');
  const [subtitles, setSubtitles] = useState({});
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [currentLanguage, setCurrentLanguage] = useState('');
  const [playedProgress, setPlayedProgress] = useState(0);
  const [transitionsArray, setTransitionsArray] = useState([]);
  const [textPanels, setTextPanels] = useState([]);
  const [mixedOrientation, setMixedOrientation] = useState(false);
  const [externalPlaybackRate, setExternalPlaybackRate] = useState(1.0);
  const [isFirstPlayed, setIsFirstPlayed] = useState(false);
  const [maxDimensions, setMaxDimensions] = useState(null);
  const [hideControlsOnStart, setHideControlsOnStart] = useState(false);
  const [subtitlesSettings, setSubtitlesSettings] = useState(null);

  const { nodeRef: containerNode, handleNodeRef } = useNodeRef();

  const { isCallToActionEnabled } = useUserContext();

  const {
    handleStartBackgroundMusic,
    handleStopBackgroundMusic,
    onSeekCallback,
    onVolumeChange,
    onPlaybackRateChange,
  } = useBackgroundMusic({
    scenes,
    currentScene: scenes[videoIndex],
    isVideoPlaying,
    playedProgress
  });

  const { trimmingOn } = useTrimContext();

  const thumbnailResource = useMemo(() => ResourceHelper.getResourceByLabel(scenes[videoIndex] ?? {}, 'defaultStaticThumbnail'), [
    scenes,
    videoIndex,
  ]);

  const currentCallAction = useMemo(() => {
    if (!isCallToActionEnabled) {
      return null;
    }
    const playedMilliseconds = customTiming.progress * 1000;
    return videoConfig?.ctas?.find(({ offset, duration }) => offset <= playedMilliseconds && offset + duration >= playedMilliseconds);
  }, [isCallToActionEnabled, customTiming, videoConfig]);

  const handleVolumeUpdated = newVolume => {
    setVolume(newVolume);
    onVolumeChange(newVolume);
  };

  const handleExternalPlaybackRateChange = value => {
    setExternalPlaybackRate(value);
    onPlaybackRateChange(value);
  };


  const handleTogglePlaying = value => {
    setIsVideoPlaying(value);
  };

  const onEnded = useCallback(() => {
    if (videoIndex === scenes.length - 1) {
      setIsVideoPlaying(false);
    }
    !trimmingOn && handleEnded();
  }, [scenes, videoIndex, handleEnded, trimmingOn]);

  useEffect(() => {
    isVideoPlaying && !isFirstPlayed && setIsFirstPlayed(true);
  }, [isVideoPlaying]);

  useEffect(() => {
    let timerId;

    if (videoIndex === 0) {
      // We need to make sure the video is actually paused and not just a very short pause when the scene changes
      timerId = setTimeout(() => setHideControlsOnStart(isVideoPlaying), 100);
    } else {
      setHideControlsOnStart(true);
      clearTimeout(timerId);
    }
  }, [videoIndex, scenes]);

  useEffect(() => {
    // This value is true when the current scene is deleted
    // and the scene music is paused
    if (isStopBackgroundMusic) {
      setIsVideoPlaying(false);
    }
  }, [isStopBackgroundMusic]);

  useEffect(() => {
    const getSceneSubtitles = async () => {
      await Promise.allSettled(
        scenes?.map(async item => {
          const updateSubtitles = {};
          if (!(item.id === 'Intro' || item.id === 'Outro') && !subtitles.hasOwnProperty(item.id)) {
            await Promise.allSettled(
              item?.subtitlesMeta?.map(async item => {
                if (!updateSubtitles?.hasOwnProperty(item.language) && !Boolean(item.trimmed)) {
                  if (item.original && currentLanguage === '') {
                    setCurrentLanguage(item.language);
                  }
                  const response = await fetch(item.srtPath);
                  const srt = await response.text();
                  updateSubtitles[item.language] = parseSync(srt);
                }
              })
            );
            setSubtitles(prevState => ({ ...prevState, [item.id]: updateSubtitles }));
          }
        })
      );
    };
    scenes && getSceneSubtitles();
  }, [scenes]);

  useEffect(() => {
    scenes[videoIndex]?.trimStart && setPlayedProgress(scenes[videoIndex]?.trimStart / (scenes[videoIndex]?.playbackRate || 1) / 1000);
  }, [videoIndex]);

  useEffect(() => {
    if (scenes) {
      const languages = [];
      scenes?.forEach(scene => {
        if (scene.hasOwnProperty('subtitlesMeta')) {
          scene?.subtitlesMeta?.forEach(item => {
            languages.push(item.language);
          });
        }
      });
      let unique = [...new Set(languages)];
      setAvailableLanguages(unique);

      const fullVideoOrientation = calculateVideoOrientation(scenes);
      setSubtitlesSettings(SubtitlesHelper.appendMarginYForOrientation(videoConfig?.subtitlesSettings, fullVideoOrientation));

      scenes[videoIndex]?.processing && setIsVideoPlaying(false);
    }
  }, [scenes]);

  useEffect(() => {
    const getLogoPositionData = (scenes, tries = 0) => {
      if (scenes[videoIndex]?.video === null || videoIndex >= scenes.length) {
        return;
      }
      const video = videoElements.find(item => item?.sceneId === scenes[videoIndex]?.id);
      if (video && video.video?.offsetHeight > 0) {
        const filteredScenes = scenes.filter(item => item.id !== 'Intro' && item.id !== 'Outro');
        setMaxDimensions(getMaxDimensions(filteredScenes));
        setMixedOrientation(getPlayerMixedOrientation(filteredScenes));
      } else {
        setTimeout(() => {
          getLogoPositionData(scenes, tries + 1);
        }, 500);
      }
    };

    scenes.length && videoElements?.length && getLogoPositionData(scenes);
  }, [scenes, videoElements, currentPlayerKey]);

  useEffect(() => {
    let currentText = '';
    const currentScene = scenes[videoIndex];
    if (currentScene?.hasOwnProperty('subtitlesMeta') && !currentScene.recording) {
      if (subtitles[currentScene.id]?.hasOwnProperty(currentLanguage)) {
        const playedMilliseconds = playedProgress * 1000;
        const currentSubtitleItem = subtitles[currentScene.id][currentLanguage].find(
          item => playedMilliseconds >= item.data.start && playedMilliseconds <= item.data.end
        );
        if (currentSubtitleItem) {
          currentText = currentSubtitleItem.data.text;
        }
      }
    }
    setSubtitlesTextForShowing(currentText);
  }, [playedProgress, currentLanguage, subtitles, videoIndex, scenes]);

  useEffect(() => {
    if (scenes) {
      setTextPanels(
        scenes.map(item =>
          item.id !== 'Intro' && item.id !== 'Outro' && !item.recording ? generateTextPanelsArray([item], 0, true) : null
        )
      );
    }
  }, [scenes]);

  useEffect(() => {
    if (scenes && transitions) {
      const introDuration = scenes.find(item => item.id === 'Intro')?.trimmedDuration;
      const generatedTransitions = generateTransitionsArray(
        scenes.filter(item => item.id !== 'Intro' && item.id !== 'Outro'),
        transitions,
        introDuration,
        true
      );
      setTransitionsArray(generatedTransitions);
    }
  }, [scenes, transitions]);

  return (
    <div className={cx(styles.videoContainer, className)}>
      <div className={styles.scrollContainer}>
        <div ref={handleNodeRef} className={styles.videoContent} id="clip-organizer-player">
          {videoElements?.length > 0 &&
            videoElements.map(item => (
              <PlayerComponent
                key={item.key}
                playerKey={item.key}
                sceneId={item.sceneId}
                scenes={scenes}
                currentSceneIndex={videoIndex}
                currentPlayerKey={currentPlayerKey}
                handleEnded={onEnded}
                setPlayedProgress={setPlayedProgress}
                customTiming={customTiming}
                externalVolume={volume}
                handleVolumeUpdated={handleVolumeUpdated}
                setIsControlsVisible={setIsControlsVisible}
                handleTogglePlaying={handleTogglePlaying}
                isVideoPlaying={isVideoPlaying}
                currentLanguage={currentLanguage}
                availableLanguages={availableLanguages}
                setCurrentLanguage={setCurrentLanguage}
                textPanels={textPanels}
                isFirstPlayed={isFirstPlayed}
                isDraft={isDraft}
                hideControlsOnStart={hideControlsOnStart}
                thumbnailResource={thumbnailResource}
                onSeekCallback={onSeekCallback}
                externalPlaybackRate={externalPlaybackRate}
                handleExternalPlaybackRateChange={handleExternalPlaybackRateChange}
                userPermissions={userPermissions}
                draftComponent={draftComponent}
                rerecordComponent={rerecordComponent}
                handleStartBackgroundMusic={handleStartBackgroundMusic}
                handleStopBackgroundMusic={handleStopBackgroundMusic}
                miniRightSidebar={miniRightSidebar}
                isEditingRightSidebar={isEditingRightSidebar}
                videoConfig={{ ...structuredClone(videoConfig), logo: null }}
              />
            ))}
          {!trimmingOn && (
            <TransitionSimulation
              isVideoPlaying={isVideoPlaying}
              transitions={transitionsArray}
              playedMs={customTiming.progress * 1000}
              currentVideoElement={videoElements?.find(item => item.key === currentPlayerKey)}
              hideOnVideoPaused={true}
            />
          )}
          <VideoSubtitles
            subtitlesSettings={subtitlesSettings}
            text={subtitlesTextForShowing}
            videoControlsVisible={isFirstPlayed ? isControlsVisible : false}
            sceneId={scenes[videoIndex]?.id}
          />
          <VideoLogo
            parentNode={containerNode}
            id={scenes[videoIndex]?.id}
            logo={logo}
            played={customTiming.progress * 1000}
            intro={intro}
            outro={outro}
            duration={customTiming.duration * 1000}
            mixedOrientation={mixedOrientation}
            videoMaxDimensions={maxDimensions}
            multiplyMode={true}
            isEmptyVideo={scenes[videoIndex]?.video === null}
            isMultiplyMode={true}
          />
          {currentCallAction && (
            <VideoCallToAction
              parentNode={containerNode}
              callAction={currentCallAction}
              id={scenes[videoIndex]?.id}
              currentSeconds={customTiming?.progress * 1000}
              mixedOrientation={mixedOrientation}
              videoMaxDimensions={maxDimensions}
              onPauseCallback={() => setIsVideoPlaying(false)}
              multiplyMode={true}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default MultipleVideosPlayer;
